import React from 'react'
import { Button, Heading, Text, LoadingSpinner } from '../../components/ui'
import { PermissionGuard } from '../../components/PermissionGuard'
import { usePermission } from '../../hooks/usePermissions'
import { modulesService } from '../../services/modules'
import { comercialCadastroService } from '../../services/comercialCadastro'
import type { ComercialLookupInput, ComercialLookupItem } from '../../types/comercialCadastro'
import { PencilIcon, TrashIcon, PlusIcon } from '@heroicons/react/24/outline'

const MODULE_NAME = 'comercial_cadastro'

type MainTab = 'comercial' | 'parceiro'
type ComercialSubTab = 'tipo_atividade' | 'objetivo_visita'
type ParceiroSubTab = 'motivo_contato'
type ActiveTab = ComercialSubTab | ParceiroSubTab

const TAB_LABELS: Record<ActiveTab, string> = {
  tipo_atividade: 'Tipo de Atividade',
  objetivo_visita: 'Objetivo Visita',
  motivo_contato: 'Motivo Contato',
}

const EMPTY_INPUT: ComercialLookupInput = { name: '' }

function formatDate(value: string): string {
  return new Date(value).toLocaleDateString('pt-BR')
}

const ComercialCadastro: React.FC = () => {
  const [activeMainTab, setActiveMainTab] = React.useState<MainTab>('comercial')
  const [comercialSubTab, setComercialSubTab] = React.useState<ComercialSubTab>('tipo_atividade')
  const [parceiroSubTab, setParceiroSubTab] = React.useState<ParceiroSubTab>('motivo_contato')
  const activeTab: ActiveTab =
    activeMainTab === 'comercial' ? comercialSubTab : parceiroSubTab
  const [loading, setLoading] = React.useState(true)
  const [pageError, setPageError] = React.useState<string | null>(null)

  const [activityTypes, setActivityTypes] = React.useState<ComercialLookupItem[]>([])
  const [visitObjectives, setVisitObjectives] = React.useState<ComercialLookupItem[]>([])
  const [contactReasons, setContactReasons] = React.useState<ComercialLookupItem[]>([])

  const [filterActivityTypes, setFilterActivityTypes] = React.useState('')
  const [filterVisitObjectives, setFilterVisitObjectives] = React.useState('')
  const [filterContactReasons, setFilterContactReasons] = React.useState('')

  const [moduleInfo, setModuleInfo] = React.useState<{ display_name: string; description: string | null } | null>(null)

  const [formModal, setFormModal] = React.useState<{
    type: ActiveTab
    data: ComercialLookupInput
  } | null>(null)
  const [submittingForm, setSubmittingForm] = React.useState(false)
  const [formError, setFormError] = React.useState<string | null>(null)

  const [deleteModal, setDeleteModal] = React.useState<{
    type: ActiveTab
    id: string
    label: string
  } | null>(null)
  const [submittingDelete, setSubmittingDelete] = React.useState(false)

  const { hasPermission: canInsert } = usePermission(MODULE_NAME, 'insert')
  const { hasPermission: canUpdate } = usePermission(MODULE_NAME, 'update')
  const { hasPermission: canDelete } = usePermission(MODULE_NAME, 'delete')

  const getErrorMessage = (error: unknown): string => {
    if (error instanceof Error) return error.message
    const maybe = error as { message?: string; code?: string }
    if (maybe?.message) return maybe.message
    if (maybe?.code) return `Erro (${maybe.code})`
    return 'Erro inesperado ao processar solicitação.'
  }

  const loadData = React.useCallback(async () => {
    setLoading(true)
    setPageError(null)
    try {
      const [types, objectives, reasons] = await Promise.all([
        comercialCadastroService.listActivityTypes(),
        comercialCadastroService.listVisitObjectives(),
        comercialCadastroService.listContactReasons(),
      ])
      setActivityTypes(types)
      setVisitObjectives(objectives)
      setContactReasons(reasons)
    } catch (error) {
      setPageError(getErrorMessage(error))
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => {
    void loadData()
  }, [loadData])

  React.useEffect(() => {
    const loadModule = async () => {
      const result = await modulesService.getByName(MODULE_NAME)
      if (result.success && result.data) {
        setModuleInfo({
          display_name: result.data.display_name || 'Comercial • Cadastro',
          description: result.data.description || null,
        })
      }
    }
    void loadModule()
  }, [])

  const openNew = (type: ActiveTab) => {
    setFormError(null)
    setFormModal({ type, data: { ...EMPTY_INPUT } })
  }

  const openEdit = (type: ActiveTab, item: ComercialLookupItem) => {
    setFormError(null)
    setFormModal({
      type,
      data: { id: item.id, name: item.name },
    })
  }

  const closeFormModal = () => {
    if (submittingForm) return
    setFormModal(null)
    setFormError(null)
  }

  const handleFormSave = async () => {
    if (!formModal) return
    if (!formModal.data.name.trim()) {
      setFormError('O nome é obrigatório.')
      return
    }
    setSubmittingForm(true)
    setFormError(null)
    try {
      if (formModal.type === 'tipo_atividade') {
        await comercialCadastroService.upsertActivityType(formModal.data)
      } else if (formModal.type === 'objetivo_visita') {
        await comercialCadastroService.upsertVisitObjective(formModal.data)
      } else {
        await comercialCadastroService.upsertContactReason(formModal.data)
      }
      closeFormModal()
      await loadData()
    } catch (error) {
      setFormError(getErrorMessage(error))
    } finally {
      setSubmittingForm(false)
    }
  }

  const openDeleteModal = (type: ActiveTab, id: string, label: string) => {
    setDeleteModal({ type, id, label })
  }

  const closeDeleteModal = () => {
    if (submittingDelete) return
    setDeleteModal(null)
  }

  const confirmDelete = async () => {
    if (!deleteModal) return
    setSubmittingDelete(true)
    try {
      if (deleteModal.type === 'tipo_atividade') {
        await comercialCadastroService.softDeleteActivityType(deleteModal.id)
        setActivityTypes((prev) => prev.filter((item) => item.id !== deleteModal.id))
      } else if (deleteModal.type === 'objetivo_visita') {
        await comercialCadastroService.softDeleteVisitObjective(deleteModal.id)
        setVisitObjectives((prev) => prev.filter((item) => item.id !== deleteModal.id))
      } else {
        await comercialCadastroService.softDeleteContactReason(deleteModal.id)
        setContactReasons((prev) => prev.filter((item) => item.id !== deleteModal.id))
      }
      closeDeleteModal()
    } catch (error) {
      setPageError(getErrorMessage(error))
    } finally {
      setSubmittingDelete(false)
    }
  }

  const mainTabClass = (tab: MainTab) =>
    `py-3 px-1 border-b-2 font-medium text-sm ${
      activeMainTab === tab
        ? 'border-primary-600 text-primary-600 dark:text-primary-400'
        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
    }`

  const subTabClass = (active: boolean) =>
    `py-2.5 px-1 border-b-2 font-medium text-sm ${
      active
        ? 'border-primary-600 text-primary-600 dark:text-primary-400'
        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
    }`

  const renderTable = (
    type: ActiveTab,
    items: ComercialLookupItem[],
    emptyLabel: string,
    newLabel: string,
    filter: string,
    setFilter: (value: string) => void,
  ) => {
    const filtered = filter.trim()
      ? items.filter((item) => item.name.toLowerCase().includes(filter.trim().toLowerCase()))
      : items

    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white flex-shrink-0">
              {TAB_LABELS[type]}
            </Heading>
            <span className="text-sm text-gray-500 dark:text-gray-400 flex-shrink-0">
              {filtered.length}
              {filter.trim() && filtered.length !== items.length ? ` de ${items.length}` : ''}{' '}
              {filtered.length === 1 ? 'registro' : 'registros'}
            </span>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <input
              type="text"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filtrar por nome..."
              className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 w-48"
            />
            {canInsert && (
              <Button variant="solid" size="md" onClick={() => openNew(type)}>
                <PlusIcon className="h-4 w-4" />
                {newLabel}
              </Button>
            )}
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <Text size="sm" color="muted">
              {filter.trim() ? `Nenhum resultado para "${filter}".` : emptyLabel}
            </Text>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Nome
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Criado em
                  </th>
                  {(canUpdate || canDelete) && (
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Ações
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {filtered.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">
                      {item.name}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
                      {formatDate(item.created_at)}
                    </td>
                    {(canUpdate || canDelete) && (
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-2">
                          {canUpdate && (
                            <button
                              type="button"
                              title="Editar"
                              onClick={() => openEdit(type, item)}
                              className="text-gray-400 hover:text-primary-500 dark:hover:text-primary-400 transition-colors"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                          )}
                          {canDelete && (
                            <button
                              type="button"
                              title="Excluir"
                              onClick={() => openDeleteModal(type, item.id, item.name)}
                              className="text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    )
  }

  return (
    <>
      <main className="flex-1 flex flex-col overflow-x-hidden overflow-y-auto bg-gray-100 dark:bg-gray-900">
        <PermissionGuard
          moduleName={MODULE_NAME}
          permissionType="show"
          fallback={
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <Text size="xl" className="text-gray-500 dark:text-gray-400 mb-2">Acesso Negado</Text>
                <Text size="sm" color="muted">Você não tem permissão para acessar esta página.</Text>
              </div>
            </div>
          }
        >
          <div className="h-full flex flex-col">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <Heading as="h1" size="3xl" className="text-gray-900 dark:text-white">
                {moduleInfo?.display_name || 'Comercial • Cadastro'}
              </Heading>
              {moduleInfo?.description && (
                <Text size="sm" color="muted" className="mt-1">
                  {moduleInfo.description}
                </Text>
              )}
            </div>

            <div className="px-6 pt-4 border-b border-gray-200 dark:border-gray-700">
              <div className="border-b border-gray-200 dark:border-gray-700">
                <nav className="-mb-px flex space-x-8">
                  <button type="button" onClick={() => setActiveMainTab('comercial')} className={mainTabClass('comercial')}>
                    Comercial
                  </button>
                  <button type="button" onClick={() => setActiveMainTab('parceiro')} className={mainTabClass('parceiro')}>
                    Parceiro
                  </button>
                </nav>
              </div>
            </div>

            <div className="px-6 pt-3 border-b border-gray-200 dark:border-gray-700">
              <nav className="-mb-px flex space-x-6">
                {activeMainTab === 'comercial' ? (
                  <>
                    <button
                      type="button"
                      onClick={() => setComercialSubTab('tipo_atividade')}
                      className={subTabClass(comercialSubTab === 'tipo_atividade')}
                    >
                      Tipo de Atividade
                    </button>
                    <button
                      type="button"
                      onClick={() => setComercialSubTab('objetivo_visita')}
                      className={subTabClass(comercialSubTab === 'objetivo_visita')}
                    >
                      Objetivo Visita
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => setParceiroSubTab('motivo_contato')}
                    className={subTabClass(parceiroSubTab === 'motivo_contato')}
                  >
                    Motivo Contato
                  </button>
                )}
              </nav>
            </div>

            {loading ? (
              <div className="flex-1 flex items-center justify-center">
                <LoadingSpinner />
              </div>
            ) : (
              <div className="p-6 space-y-4">
                {pageError && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
                    <Text size="sm">{pageError}</Text>
                  </div>
                )}

                {activeTab === 'tipo_atividade' && renderTable(
                  'tipo_atividade',
                  activityTypes,
                  'Nenhum tipo de atividade cadastrado.',
                  'Novo Tipo',
                  filterActivityTypes,
                  setFilterActivityTypes,
                )}

                {activeTab === 'objetivo_visita' && renderTable(
                  'objetivo_visita',
                  visitObjectives,
                  'Nenhum objetivo de visita cadastrado.',
                  'Novo Objetivo',
                  filterVisitObjectives,
                  setFilterVisitObjectives,
                )}

                {activeTab === 'motivo_contato' && renderTable(
                  'motivo_contato',
                  contactReasons,
                  'Nenhum motivo de contato cadastrado.',
                  'Novo Motivo',
                  filterContactReasons,
                  setFilterContactReasons,
                )}
              </div>
            )}
          </div>
        </PermissionGuard>
      </main>

      {formModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-lg mx-4 p-6">
            <div className="flex items-center justify-between mb-5">
              <Text size="lg" className="font-semibold text-gray-900 dark:text-white">
                {formModal.data.id
                  ? `Editar ${TAB_LABELS[formModal.type]}`
                  : `Novo ${TAB_LABELS[formModal.type]}`}
              </Text>
              <Button variant="ghost" onClick={closeFormModal} disabled={submittingForm}>
                Fechar
              </Button>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Nome <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formModal.data.name}
                onChange={(e) =>
                  setFormModal((prev) =>
                    prev ? { ...prev, data: { ...prev.data, name: e.target.value } } : prev
                  )
                }
                placeholder="Informe o nome..."
                className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            {formError && (
              <div className="mt-3 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
                <Text size="sm">{formError}</Text>
              </div>
            )}

            <div className="flex justify-end gap-3 mt-6">
              <Button variant="ghost" onClick={closeFormModal} disabled={submittingForm}>
                Cancelar
              </Button>
              <Button variant="solid" onClick={handleFormSave} disabled={submittingForm}>
                {submittingForm ? 'Salvando...' : 'Salvar'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {deleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
            <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-2">
              Confirmar Exclusão
            </Heading>
            <Text size="sm" color="muted" className="mb-4">
              Você está excluindo: <strong className="text-gray-900 dark:text-white">{deleteModal.label}</strong>
              <br />
              O registro não será removido do banco de dados (exclusão lógica).
            </Text>

            <div className="flex justify-end gap-3 mt-4">
              <Button variant="ghost" onClick={closeDeleteModal} disabled={submittingDelete}>
                Cancelar
              </Button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={submittingDelete}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-50 transition-colors"
              >
                {submittingDelete ? 'Excluindo...' : 'Confirmar Exclusão'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default ComercialCadastro
